import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Eye, Code, AlertCircle, Edit, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from "@/components/ui/alert";
import CustomerService from '@/api/customers.service';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Directly include mock visual editor until complete implementation is ready
interface VisualEditorProps {
  initialContent: string;
  onContentChange: (content: string) => void;
  subject?: string;
  onSubjectChange?: (subject: string) => void;
  type: 'email' | 'sms' | 'whatsapp';
}

const VisualEditor: React.FC<VisualEditorProps> = ({ 
  initialContent,
  onContentChange,
  subject,
  onSubjectChange,
  type
}) => {
  // Add basic subject input for email type
  const [editedContent, setEditedContent] = useState(initialContent);
  const [editedSubject, setEditedSubject] = useState(subject || '');
  
  // Update when props change
  useEffect(() => {
    setEditedContent(initialContent);
  }, [initialContent]);
  
  useEffect(() => {
    if (subject !== undefined) {
      setEditedSubject(subject);
    }
  }, [subject]);
  
  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSubject = e.target.value;
    setEditedSubject(newSubject);
    if (onSubjectChange) {
      onSubjectChange(newSubject);
    }
  };
  
  const handleSave = () => {
    onContentChange(editedContent);
  };
  
  return (
    <div className="p-6">
      {type === 'email' && (
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Subject</label>
          <input 
            type="text" 
            value={editedSubject} 
            onChange={handleSubjectChange}
            className="w-full p-2 border rounded-md"
            placeholder="Email subject..."
          />
        </div>
      )}
      
      <h3 className="text-lg font-medium mb-4">Visual Editor</h3>
      <p className="text-muted-foreground mb-6">
        Visual editor is currently in development. A full implementation would include drag-and-drop elements, 
        text formatting, color pickers, and more.
      </p>
      <div className="mb-6 border rounded-lg p-4 bg-gray-50">
        <iframe 
          srcDoc={initialContent}
          className="w-full h-[400px] border-0"
          title="Email preview"
          sandbox="allow-same-origin"
        />
      </div>
      <div className="flex justify-end">
        <Button onClick={handleSave}>
          Save Changes
        </Button>
      </div>
    </div>
  );
};

interface CampaignPreviewProps {
  type: 'email' | 'sms' | 'whatsapp';
  subject?: string;
  content: string;
  recipientCount: number;
  recipientIds?: string[];
  businessId?: number;
  onClose: () => void;
  onContentChange?: (newContent: string) => void;
  onSubjectChange?: (newSubject: string) => void;
  signature?: any;
}

const CampaignPreview: React.FC<CampaignPreviewProps> = ({ 
  type, 
  subject, 
  content, 
  recipientCount,
  recipientIds = [],
  businessId,
  onClose,
  onContentChange,
  onSubjectChange,
  signature
}) => {
  const [viewMode, setViewMode] = useState<'preview' | 'code' | 'visual'>(type === 'email' ? 'preview' : 'preview');
  const [currentRecipientIndex, setCurrentRecipientIndex] = useState(0);
  const [recipients, setRecipients] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });
  
  useEffect(() => {
    const fetchRecipients = async () => {
      if (recipientIds && recipientIds.length > 0) {
        setLoading(true);
        try {
          // Only fetch first 10 recipients for preview purposes
          const recipientsToFetch = recipientIds.slice(0, 10);
          const recipientPromises = recipientsToFetch.map(id => 
            CustomerService.getCustomer(id)
              .then(response => response.data.result === 'success' ? response.data.customer : null)
              .catch(error => {
                console.error(`Error fetching customer ${id}:`, error);
                return null;
              })
          );
          
          const fetchedRecipients = await Promise.all(recipientPromises);
          setRecipients(fetchedRecipients.filter(r => r !== null));
        } catch (error) {
          console.error("Error fetching recipients:", error);
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchRecipients();
  }, [recipientIds]);
  
  const nextRecipient = () => {
    if (currentRecipientIndex < recipients.length - 1) {
      setCurrentRecipientIndex(currentRecipientIndex + 1);
    }
  };
  
  const prevRecipient = () => {
    if (currentRecipientIndex > 0) {
      setCurrentRecipientIndex(currentRecipientIndex - 1);
    }
  };
  
  // Modify the processContent function to include the signature if provided
  const processContent = (content: string) => {
    if (!content) return '';
    
    let processed = content;
    const currentRecipient = recipients[currentRecipientIndex];
    
    // Replace customer variables with actual values if available
    if (currentRecipient) {
      processed = processed
        .replace(/\{\{firstName\}\}/g, currentRecipient.firstName || 'FirstName')
        .replace(/\{\{lastName\}\}/g, currentRecipient.lastName || 'LastName')
        .replace(/\{\{phoneNumber\}\}/g, currentRecipient.phoneNumber || 'PhoneNumber')
        .replace(/\{\{emailAddress\}\}/g, currentRecipient.emailAddress || 'Email')
        .replace(/\{\{dateOfBirth\}\}/g, currentRecipient.dateOfBirth ? new Date(currentRecipient.dateOfBirth).toLocaleDateString() : 'DOB')
        .replace(/\{\{street\}\}/g, currentRecipient.street || 'Street')
        .replace(/\{\{city\}\}/g, currentRecipient.city || 'City')
        .replace(/\{\{state\}\}/g, currentRecipient.state || 'State')
        .replace(/\{\{zipCode\}\}/g, currentRecipient.zipCode || 'ZipCode')
        .replace(/\{\{country\}\}/g, currentRecipient.country || 'Country')
        .replace(/\{\{companyName\}\}/g, currentRecipient.companyName || 'Company');
    } else {
      // Fallback placeholders if recipient data isn't available
      processed = processed
        .replace(/\{\{firstName\}\}/g, 'FirstName')
        .replace(/\{\{lastName\}\}/g, 'LastName')
        .replace(/\{\{phoneNumber\}\}/g, 'PhoneNumber')
        .replace(/\{\{emailAddress\}\}/g, 'Email')
        .replace(/\{\{dateOfBirth\}\}/g, 'DOB')
        .replace(/\{\{street\}\}/g, 'Street')
        .replace(/\{\{city\}\}/g, 'City')
        .replace(/\{\{state\}\}/g, 'State')
        .replace(/\{\{zipCode\}\}/g, 'ZipCode')
        .replace(/\{\{country\}\}/g, 'Country')
        .replace(/\{\{companyName\}\}/g, 'Company');
    }
    
    // Replace business variables
    if (selectedBusiness) {
      processed = processed
        .replace(/\{\{company\}\}/g, selectedBusiness.name || 'Your Business')
        .replace(/\{\{businessName\}\}/g, selectedBusiness.name || 'Your Business')
        .replace(/\{\{businessEmail\}\}/g, selectedBusiness.email || 'business@example.com')
        .replace(/\{\{businessPhone\}\}/g, selectedBusiness.phone || '(123) 456-7890')
        .replace(/\{\{businessAddress\}\}/g, selectedBusiness.address || 'Business Address');
    } else {
      processed = processed
        .replace(/\{\{company\}\}/g, 'Your Business')
        .replace(/\{\{businessName\}\}/g, 'Your Business')
        .replace(/\{\{businessEmail\}\}/g, 'business@example.com')
        .replace(/\{\{businessPhone\}\}/g, '(123) 456-7890')
        .replace(/\{\{businessAddress\}\}/g, 'Business Address');
    }
    
    // Replace common template variables
    processed = processed
      .replace(/\{\{year\}\}/g, new Date().getFullYear().toString())
      .replace(/\[\[expiryDate\]\]/g, '31 Dec 2023')
      .replace(/\[\[month\]\]/g, 'November')
      .replace(/\[\[discount\]\]/g, '20%')
      .replace(/\[\[code\]\]/g, 'SAVE20')
      .replace(/\[\[date\]\]/g, 'Nov 25')
      .replace(/\[\[time\]\]/g, '3:00 PM')
      .replace(/\[\[orderNumber\]\]/g, '12345')
      .replace(/\[\[status\]\]/g, 'shipped')
      .replace(/\[\[additionalInfo\]\]/g, 'Expected delivery in 2-3 days.')
      .replace(/\[\[trackingLink\]\]/g, 'https://example.com/track')
      .replace(/\[\[reviewLink\]\]/g, 'https://example.com/review')
      .replace(/\[\[offer\]\]/g, '25% Off Your Next Purchase');

    // Add signature at the bottom if provided and in email mode
    if (type === 'email' && signature) {
      // Create a wrapper to ensure content has proper spacing before signature
      const contentWithSignature = `
        <div>
          ${processed}
          ${processed.includes('</div>') ? '' : '<br/><br/>'}
          <div style="margin-top: 20px; border-top: 1px solid #eaeaea; padding-top: 10px;">
            ${signature.signatureType === 'image' || signature.signatureType === 'both' ? 
              signature.imageLink ? `<div><img src="${signature.imageLink}" alt="Signature" style="max-height: 80px; max-width: 300px;"/></div>` : ''
            : ''}
            ${signature.signatureType === 'text' || signature.signatureType === 'both' ? 
              `<div>${signature.content || ''}</div>` : ''
            }
            ${signature.includeDisclaimer && signature.disclaimer ? 
              `<div style="font-size: 11px; color: #666; margin-top: 10px;">${signature.disclaimer}</div>` : ''
            }
          </div>
        </div>
      `;
      return contentWithSignature;
    }
    
    return processed;
  };
  
  const processedContent = processContent(content);
  const processedSubject = subject ? processContent(subject) : '';
  
  const handleContentChange = (newContent: string) => {
    if (onContentChange) {
      onContentChange(newContent);
    }
  };
  
  const handleSubjectChange = (newSubject: string) => {
    if (onSubjectChange) {
      onSubjectChange(newSubject);
    }
  };
  
  const renderRecipientNavigation = () => {
    if (recipients.length <= 1) return null;
    
    const currentRecipient = recipients[currentRecipientIndex];
    
    return (
      <div className="flex items-center justify-between bg-muted/30 p-2 border-t">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={prevRecipient} 
          disabled={currentRecipientIndex === 0}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </Button>
        
        <div className="text-sm text-center">
          <div className="font-medium">
            {currentRecipient ? `${currentRecipient.firstName} ${currentRecipient.lastName}` : 'Loading...'}
          </div>
          <div className="text-xs text-muted-foreground">
            Recipient {currentRecipientIndex + 1} of {recipients.length}
            {recipients.length < recipientCount && ` (${recipientCount - recipients.length} more not shown)`}
          </div>
        </div>
        
        <Button 
          variant="outline" 
          size="sm" 
          onClick={nextRecipient} 
          disabled={currentRecipientIndex === recipients.length - 1}
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    );
  };
  
  return (
    <div className="bg-white border rounded-lg shadow-lg overflow-hidden max-w-4xl mx-auto">
      <div className="p-4 bg-muted border-b flex justify-between items-center">
        <h3 className="font-medium text-lg">Campaign Preview</h3>
        <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
      </div>
      
      <div className="bg-muted/50 px-4 py-2 border-b">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm text-muted-foreground">
              {type === 'email' ? 'Email' : type === 'sms' ? 'SMS' : 'WhatsApp'} preview 
              ({recipientCount} recipient{recipientCount !== 1 ? 's' : ''})
            </span>
            {type === 'email' && subject && (
              <div className="font-medium mt-1">Subject: {processedSubject}</div>
            )}
            {type === 'email' && signature && (
              <div className="text-xs text-muted-foreground mt-1">
                With signature: {signature.name}
              </div>
            )}
          </div>
          
          {type === 'email' ? (
            <div className="flex gap-2">
              <Button 
                variant={viewMode === 'preview' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setViewMode('preview')}
              >
                <Eye className="mr-1 h-4 w-4" />
                Preview
              </Button>
              <Button 
                variant={viewMode === 'visual' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setViewMode('visual')}
              >
                <Edit className="mr-1 h-4 w-4" />
                Visual Editor
              </Button>
              <Button 
                variant={viewMode === 'code' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setViewMode('code')}
              >
                <Code className="mr-1 h-4 w-4" />
                HTML
              </Button>
            </div>
          ) : null}
        </div>
      </div>
      
      <Alert className="m-4">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Variables like <code className="bg-muted px-1 rounded">&#123;&#123;firstName&#125;&#125;</code> will be replaced with each recipient's information. 
          Manual variables that need your input are wrapped in <code className="bg-muted px-1 rounded">[[brackets]]</code>.
        </AlertDescription>
      </Alert>
      
      {loading ? (
        <div className="p-12 flex justify-center">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
            <p>Loading recipient data...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="max-h-[70vh] overflow-auto">
            {type === 'email' && viewMode === 'preview' && (
              <div className="w-full h-[500px] border-0">
                <iframe
                  title="Email Preview"
                  srcDoc={processedContent}
                  className="w-full h-full border-0"
                  sandbox="allow-same-origin"
                />
              </div>
            )}
            
            {type === 'email' && viewMode === 'visual' && (
              <div className="p-4">
                <VisualEditor 
                  initialContent={content} 
                  onContentChange={handleContentChange}
                  subject={subject}
                  onSubjectChange={handleSubjectChange}
                  type={type}
                />
              </div>
            )}
            
            {type === 'email' && viewMode === 'code' && (
              <div className="p-4 bg-gray-50">
                <pre className="bg-gray-900 text-gray-100 p-4 rounded overflow-auto text-sm">
                  {processedContent}
                </pre>
              </div>
            )}
            
            {(type === 'sms' || type === 'whatsapp') && (
              <div className="p-4">
                <div className={`max-w-sm mx-auto ${type === 'whatsapp' ? 'bg-[#E5F6DF] text-black' : 'bg-blue-100'} p-4 rounded-lg`}>
                  <div className="text-sm mb-2 text-gray-500">
                    Preview ({type === 'sms' ? 'SMS' : 'WhatsApp'})
                  </div>
                  <div className="whitespace-pre-line">{processedContent}</div>
                </div>
              </div>
            )}
          </div>
          
          {renderRecipientNavigation()}
        </>
      )}
    </div>
  );
};

export default CampaignPreview; 